import { FC, useEffect, useState } from 'react';
import { trailsService } from '@/services/trails.service';
import { useToast } from '@/contexts/ToastContext';
import LoadingSpinner from './LoadingSpinner';

interface CourseSurveyModalProps {
  trailId: string;
  courseName: string;
  onSubmitted: () => void;
}

const SCORE_VALUES = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10] as const;

interface ScoreScaleProps {
  ariaLabel: string;
  legendLeft: string;
  legendRight: string;
  value: number | null;
  onChange: (value: number) => void;
}

const scoreTone = (value: number): string => {
  if (value <= 6) return 'border-rose-500/50 hover:border-rose-500 hover:bg-rose-500/10';
  if (value <= 8) return 'border-amber-500/50 hover:border-amber-500 hover:bg-amber-500/10';
  return 'border-emerald-500/50 hover:border-emerald-500 hover:bg-emerald-500/10';
};

const scoreToneSelected = (value: number): string => {
  if (value <= 6) return 'bg-rose-500 border-rose-500 text-white';
  if (value <= 8) return 'bg-amber-500 border-amber-500 text-white';
  return 'bg-emerald-500 border-emerald-500 text-white';
};

const ScoreScale: FC<ScoreScaleProps> = ({ ariaLabel, legendLeft, legendRight, value, onChange }) => {
  return (
    <div>
      <div
        role="radiogroup"
        aria-label={ariaLabel}
        className="flex flex-wrap gap-2 justify-center"
      >
        {SCORE_VALUES.map((v) => {
          const isSelected = value === v;
          return (
            <button
              key={v}
              type="button"
              role="radio"
              aria-checked={isSelected}
              aria-label={`Nota ${v}`}
              onClick={() => onChange(v)}
              className={`min-w-[40px] h-10 px-2 rounded-lg border-2 font-bold text-sm transition-all ${
                isSelected
                  ? scoreToneSelected(v)
                  : `bg-transparent text-content-primary ${scoreTone(v)}`
              }`}
            >
              {v}
            </button>
          );
        })}
      </div>
      <div className="flex justify-between text-xs text-content-tertiary mt-2 px-1">
        <span>{legendLeft}</span>
        <span>{legendRight}</span>
      </div>
    </div>
  );
};

const CourseSurveyModal: FC<CourseSurveyModalProps> = ({ trailId, courseName, onSubmitted }) => {
  const [csat, setCsat] = useState<number | null>(null);
  const [nps, setNps] = useState<number | null>(null);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { showToast } = useToast();

  // Trava o scroll do body enquanto o modal está aberto.
  useEffect(() => {
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = original;
    };
  }, []);

  // Bloqueia Esc explicitamente — o modal é não-fechável.
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, []);

  const canSubmit = csat !== null && nps !== null && !submitting;

  const handleSubmit = async () => {
    if (csat === null || nps === null) return;
    setSubmitting(true);
    try {
      await trailsService.submitCourseSurvey(trailId, {
        csat_score: csat,
        nps_score: nps,
        comment: comment.trim(),
      });
      onSubmitted();
    } catch {
      showToast('Não foi possível enviar sua avaliação. Tente novamente.', 'error');
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      data-testid="course-survey-modal"
      // Backdrop não fecha — modal é bloqueante.
    >
      <div
        className="w-full max-w-2xl bg-surface-secondary border border-white/10 rounded-2xl shadow-2xl overflow-y-auto max-h-[90vh]"
        role="dialog"
        aria-modal="true"
        aria-labelledby="course-survey-title"
      >
        <div className="p-6 md:p-8 border-b border-white/5 bg-gradient-to-br from-accent/10 to-transparent">
          <p className="text-accent text-xs font-bold uppercase tracking-[0.2em] mb-2">
            Conte como foi o curso
          </p>
          <h2 id="course-survey-title" className="text-2xl md:text-3xl font-black text-content-primary">
            {courseName}
          </h2>
          <p className="text-content-tertiary text-sm mt-3">
            Sua opinião nos ajuda a melhorar. Leva menos de um minuto.
          </p>
        </div>

        <div className="p-6 md:p-8 flex flex-col gap-8">
          <div>
            <label className="block text-content-primary font-semibold mb-1">
              Quão satisfeito você ficou com este curso?
            </label>
            <p className="text-content-tertiary text-sm mb-4">
              Dê uma nota de 0 a 10.
            </p>
            <ScoreScale
              ariaLabel="Satisfação com o curso"
              legendLeft="Nada satisfeito"
              legendRight="Muito satisfeito"
              value={csat}
              onChange={setCsat}
            />
          </div>

          <div>
            <label className="block text-content-primary font-semibold mb-1">
              De 0 a 10, o quanto você recomendaria este curso a um colega?
            </label>
            <p className="text-content-tertiary text-sm mb-4">
              Pense em alguém que poderia se beneficiar deste conteúdo.
            </p>
            <ScoreScale
              ariaLabel="Recomendação a um colega"
              legendLeft="Não recomendaria"
              legendRight="Recomendaria muito"
              value={nps}
              onChange={setNps}
            />
          </div>

          <div>
            <label htmlFor="course-survey-comment" className="block text-content-primary font-semibold mb-1">
              Quer deixar um comentário? <span className="text-content-tertiary font-normal">(opcional)</span>
            </label>
            <textarea
              id="course-survey-comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              maxLength={1000}
              rows={3}
              className="w-full bg-surface-primary border border-white/10 rounded-lg p-3 text-content-primary placeholder-content-tertiary focus:outline-none focus:border-accent resize-none"
              placeholder="O que você gostou? O que pode melhorar?"
            />
            <p className="text-xs text-content-tertiary text-right mt-1">
              {comment.length}/1000
            </p>
          </div>

          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            data-testid="course-survey-submit"
            className="h-12 bg-accent hover:bg-accent-hover text-accent-contrast rounded-xl font-bold transition-all shadow-lg shadow-accent/20 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {submitting && <LoadingSpinner size="small" />}
            {submitting ? 'Enviando...' : 'Enviar avaliação'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CourseSurveyModal;
